import { Component, inject, OnInit } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { Auth, authState } from '@angular/fire/auth';
import { filter, take } from 'rxjs/operators';
import { SubscriptionService } from './services/subscription/subscription.service';
import { UserService } from './services/user/user.service';
import { OnboardingService } from './services/onboarding/onboarding.service';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { environment } from '../environments/environment';

interface AppPage {
  title: string;
  url: string;
  icon: string;
  premium: boolean;
}

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  private auth = inject(Auth);
  private subscriptionService = inject(SubscriptionService);
  private userService = inject(UserService);
  private onboardingService = inject(OnboardingService);

  appPages: AppPage[] = [
    { title: 'Gallery', url: '/gallery', icon: 'images', premium: false },
    { title: 'Reminders', url: '/reminders', icon: 'alarm', premium: false },
    { title: 'Corals', url: '/coral-list', icon: 'camera', premium: true },
    { title: 'Timeline', url: '/timeline', icon: 'time', premium: true },
    { title: 'Parameters', url: '/parameters', icon: 'ellipse', premium: true },
    { title: 'Coral Tests', url: '/coral-parameters', icon: 'analytics', premium: true },
    { title: 'Settings', url: '/settings', icon: 'settings', premium: false },
  ];

  constructor() {
    this.initializeApp();
  }

  ngOnInit(): void {
    // Initialize RevenueCat when user is authenticated
    authState(this.auth).pipe(
      filter(user => user !== null),
      take(1)
    ).subscribe(async (user) => {
      if (user) {
        // Ensure user profile exists
        await this.userService.ensureUserProfile(user);

        // Configure RevenueCat with user ID (works on both native and web)
        await this.subscriptionService.configure(user.uid);

        // Check and trigger premium onboarding after a short delay
        // to ensure the app has fully loaded
        setTimeout(() => {
          this.onboardingService.checkAndTriggerPremiumOnboarding();
        }, 500);
      }
    });
  }

  async initializeApp() {
    // Initialize Live Updates on native platforms (production only)
    // In development, we skip this to ensure local changes are always visible
    if (Capacitor.isNativePlatform() && environment.production) {
      await this.initializeLiveUpdate();
    } else if (Capacitor.isNativePlatform() && !environment.production) {
      // In development, reset to default bundle to ensure local changes are visible
      await this.resetLiveUpdateForDevelopment();
    }

    // Only configure status bar on native platforms
    if (Capacitor.isNativePlatform()) {
      try {
        const isIOS = Capacitor.getPlatform() === 'ios';

        // Only enable overlay mode on iOS where safe-area-inset-top works properly
        // On Android, the WebView doesn't report safe area insets correctly with overlay mode
        await StatusBar.setOverlaysWebView({ overlay: isIOS });

        // Set status bar style to light content (white text/icons)
        // Use Style.Dark for dark text/icons on light backgrounds
        await StatusBar.setStyle({ style: Style.Light });

        // On Android, set the status bar background color to match the app theme
        if (!isIOS) {
          await StatusBar.setBackgroundColor({ color: '#3880ff' }); // Primary blue
        }
      } catch (error) {
        console.log('Status bar configuration error:', error);
      }
    }
  }

  /**
   * Initialize Capawesome Live Update (Production only)
   * This notifies the plugin that the app has loaded successfully,
   * preventing automatic rollback to the previous bundle.
   */
  private async initializeLiveUpdate(): Promise<void> {
    try {
      // Notify the plugin that the app is ready
      // This prevents rollback if readyTimeout is configured
      const result = await LiveUpdate.ready();

      if (result.rollback) {
        console.log('Live Update: App was reset to default bundle due to a previous issue');
      }

      if (result.currentBundleId) {
        console.log(`Live Update: Running bundle ${result.currentBundleId}`);
      }

      // Log device info for debugging
      const deviceId = await LiveUpdate.getDeviceId();
      console.log(`Live Update: Device ID ${deviceId.deviceId}`);

      const versionName = await LiveUpdate.getVersionName();
      console.log(`Live Update: App version ${versionName.versionName}`);
    } catch (error) {
      console.log('Live Update initialization error:', error);
    }
  }

  /**
   * Reset Live Update for development
   * This ensures local changes are always visible during development
   * by resetting to the default bundle (your local build)
   */
  private async resetLiveUpdateForDevelopment(): Promise<void> {
    try {
      const currentBundle = await LiveUpdate.getCurrentBundle();

      // If a live update bundle is active, reset to default
      if (currentBundle.bundleId) {
        console.log(`[DEV] Live Update: Resetting from bundle ${currentBundle.bundleId} to default bundle`);
        await LiveUpdate.reset();
        // Note: The reset takes effect on next app launch, not immediately
        // For immediate effect during development, reinstall the app
      } else {
        console.log('[DEV] Live Update: Already using default bundle (local build)');
      }

      // Still call ready() to prevent any timeout issues
      await LiveUpdate.ready();
    } catch (error) {
      console.log('[DEV] Live Update reset error:', error);
    }
  }
}
